import { useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';

type Props = {
  show: boolean;
  // The SAVED name: the one the admin must type.
  campaignName: string;
  // What this save changes that players will feel (computed from the saved vs edited campaign).
  changes: string[];
  disabling: boolean;
  // The campaign started after the editor loaded it (the server refused the unconfirmed save).
  startedMeanwhile: boolean;
  // 'notStarted': the plain save confirmation of a campaign that has not started yet, carrying the start-push line;
  // no typed name and no running-campaign warnings.
  mode?: 'running' | 'notStarted';
  startPushNotice?: string;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * The strict running-edit confirmation (spec §12): players are mid-cooldown and mid-pity against this campaign,
 * so a save must be deliberate. It lists what players keep and requires typing the campaign name; the save then
 * goes out with confirmRunningEdit: true (the server refuses a RUNNING edit without it).
 */
export function GachaRunningEditModal({
  show,
  campaignName,
  changes,
  disabling,
  startedMeanwhile,
  mode = 'running',
  startPushNotice,
  saving,
  onCancel,
  onConfirm,
}: Props): JSX.Element {
  const [typed, setTyped] = useState('');
  const matches = typed.trim() === campaignName.trim();

  const cancel = () => {
    setTyped('');
    onCancel();
  };

  if (mode === 'notStarted') {
    return (
      <Modal show={show} onHide={cancel}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Save campaign</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {startPushNotice && (
            <p className="mb-0">
              <i className="fas fa-bell mr-2" />
              {startPushNotice}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancel}>
            Cancel
          </Button>
          <Button variant="success" disabled={saving} onClick={onConfirm}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={cancel} size="lg">
      <Modal.Header closeButton className="bg-danger text-white">
        <Modal.Title as="h5">
          <i className="fas fa-exclamation-triangle mr-2" />
          {disabling ? 'Disable a RUNNING campaign' : 'Edit a RUNNING campaign'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {startedMeanwhile && (
          <Alert variant="warning">This campaign started after you opened it: it is now RUNNING.</Alert>
        )}
        {disabling ? (
          <p>
            <strong>The campaign disappears for every player as soon as this is saved.</strong> Their state is kept and
            comes back if the campaign is enabled again.
          </p>
        ) : (
          <p>Players are already using this campaign. The save applies immediately, mid-way through their progress.</p>
        )}
        <p className="mb-1">
          <strong>What players are mid-way through, and what happens to it:</strong>
        </p>
        <ul>
          <li>
            <strong>Claims and refresh timers</strong>: each player keeps the claims of the current window. They are
            compared against the new claim limit (a player already at or above it sees the box as already claimed). A
            running refresh timer keeps its start; a new interval is counted from that start.
          </li>
          <li>
            <strong>Mystery pity</strong>: each player keeps the number of pulls; the new first / decrease / minimum
            rates apply to it. Changing the campaign start or the global recovery timer moves the recovery moments
            (players whose recovery moment changed start again from the first rate).
          </li>
          <li>
            <strong>Offered mystery rewards</strong>: a mystery reward already offered keeps the reward rolled at
            opening, whatever the new table says.
          </li>
          <li>
            <strong>Removed boxes</strong>: the players&apos; claims and pity for a removed box are orphaned. Adding a
            box back creates a new box, with fresh state.
          </li>
        </ul>
        {changes.length > 0 && (
          <>
            <p className="mb-1">
              <strong>This save changes:</strong>
            </p>
            <ul>
              {changes.map((change) => (
                <li key={change}>{change}</li>
              ))}
            </ul>
          </>
        )}
        <Form.Label htmlFor="gacha-running-edit-confirm">
          Type the campaign name <strong>{campaignName}</strong> to confirm:
        </Form.Label>
        <Form.Control
          id="gacha-running-edit-confirm"
          value={typed}
          autoComplete="off"
          isValid={matches}
          onChange={(e) => setTyped(e.target.value)}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={cancel}>
          Cancel
        </Button>
        <Button
          variant="danger"
          disabled={!matches || saving}
          onClick={() => {
            setTyped('');
            onConfirm();
          }}
        >
          {disabling ? 'Disable the running campaign' : 'Save the running campaign'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
